<template>
    <div class="wrapper"
         :class="{'-checkout': isCheckoutMode,
                  '-scrolled': scrolledMode }">
        <nav :class="{ 'fixed inset-x-0 top-0' : !isCheckoutMode }">
            <div class="navbar o-container"
                 :class="isCheckoutMode ? 'bg-transparent' : 'bg-white/[.95]'">
                <div class="navbar__logo md:mr-auto">
                    <RouterLink :to="homeLink">
                        <LogoAndTaglineHiddenOnXs/>
                    </RouterLink>
                </div>
                <template v-if="!isCheckoutMode">
                    <div class="order-first mr-auto w-[4.2] h-[4.2] md:hidden">
                        <MobileMenu/>
                    </div>
                    <div class="hidden md:block md:ml-auto">
                        <MenuItems/>
                    </div>
                    <CurrentCountryFlag class="xs:hidden md:block"/>
                </template>
                <template v-else-if="!isReceiptPage">
                    <ul class="usps">
                        <Usps/>
                    </ul>
                </template>
            </div>
        </nav>
        <div v-if="!isCheckoutMode">
            <div class="h-80 bg-white md:h-[10.3rem]"/>
            <ul class="usps">
                <Usps/>
            </ul>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { useCheckoutNavigationStates } from '@/project/navigation/checkout-navigation-states.composable';
import LogoAndTaglineHiddenOnXs from '@/project/shared/LogoAndTaglineHiddenOnXs.vue';
import Usps from '@/project/content/Usps.vue';
import MenuItems from '@/project/navigation/main-menu/MenuItems.vue';
import MobileMenu from '@/project/navigation/main-menu/MobileMenu.vue';
import { useScroll } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { PageNames } from '@/router';
import contentApi from '@/project/content/api/contentApi';
import { useCulture } from '@/project/intl/useCulture';
import CurrentCountryFlag from '@/project/navigation/main-menu/CurrentCountryFlag.vue';

export default defineComponent({
    name: 'MainNavigation',
    components: {
        Usps,
        MenuItems,
        MobileMenu,
        LogoAndTaglineHiddenOnXs,
        CurrentCountryFlag,
    },
    setup() {
        const { isCheckoutMode } = useCheckoutNavigationStates();
        const route = useRoute();
        const { getCulturePath } = useCulture();
        const isReceiptPage = computed(() => route.name === PageNames.GiftCardCheckoutReceipt);
        const { arrivedState } = useScroll(window, {
            offset: { top: 42 },
            throttle: 100,
        });

        const scrolledMode = computed(() => !arrivedState.top && !isCheckoutMode.value);

        const homeLink = computed(() => getCulturePath('/'));
        const currentCountry = ref<CountryItem | null>(null);

        contentApi.settings.then(settings => {
            const pathSegment = location.pathname.split('/')[1];
            currentCountry.value = pathSegment && settings.countries?.length
                ? settings.countries.find(c => c.path === pathSegment) ?? null
                : null;
        });

        return {
            isCheckoutMode,
            scrolledMode,
            isReceiptPage,
            homeLink,
        };
    },
});
</script>

<style scoped>

    .wrapper {
        @apply z-3;
    }

    .navbar {
        @apply relative border-b border-b-grey-100 h-80 left-0 w-full my-0 m-auto flex items-center px-20 transition-all duration-300 ease-in-out;

        .-checkout & {
            @apply border-none;
        }

        .-scrolled & {
            @apply h-60;
        }
    }

    .navbar__logo {
        @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2;
    }

    @screen md {
        .navbar__logo {
            @apply static left-auto top-auto translate-x-0 translate-y-0;
        }
    }

    .-scrolled :deep(.logo-tagline) {
        opacity: 0;
        font-size: 0;
        margin-top: 0;
    }

    .usps {
        @apply items-center justify-center hidden md:flex h-60 bg-white;

        .-checkout & {
            @apply bg-transparent;
        }
    }

    @screen lg {
        .navbar {
            height: 10.3rem;

            .-scrolled & {
                height: 6.1rem;
            }
        }

        .-scrolled :deep(.logo) {
            width: 9.75rem;
        }
    }
</style>
